import React from 'react'
import { LayoutDashboard, LogOut } from 'lucide-react'
import Link from '../framework/link'
import { ACTIONS, BUSINESS_HREF } from '../../config/site'
import { useAuth } from '../../lib/auth'

/**
 * The header's account control.
 *
 * ── IT SHOWS NOTHING UNTIL IT KNOWS ────────────────────────────────────────
 * While the session is resolving this renders a neutral placeholder of the
 * same width rather than "Sign In". Rendering the signed-out state first means
 * a signed-in visitor watches the header tell them to sign in and then change
 * its mind — and, on a slow connection, long enough to click it.
 *
 * The placeholder is `aria-hidden` and non-interactive: there is nothing there
 * to announce yet, and reserving the space keeps the header from reflowing.
 */
export function AccountMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { user, loading, configured, signOut } = useAuth()

  // Without Supabase configured there are no accounts at all, so the original
  // sign-in link is the honest thing to show.
  if (!configured) {
    return (
      <Link href={ACTIONS.signIn.href} onClick={onNavigate} className="btn-quiet">
        {ACTIONS.signIn.label}
      </Link>
    )
  }

  if (loading) {
    return (
      <span
        aria-hidden="true"
        className="inline-block h-11 w-24 rounded-btn bg-soft"
      />
    )
  }

  if (!user) {
    return (
      <Link href={ACTIONS.signIn.href} onClick={onNavigate} className="btn-quiet">
        {ACTIONS.signIn.label}
      </Link>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <Link
        href={BUSINESS_HREF}
        onClick={onNavigate}
        className="btn-quiet gap-2"
        // The email is the only identity we have; it is also long, so it is
        // the accessible name rather than visible text at this width.
        aria-label={`Your business dashboard, signed in as ${user.email ?? 'your account'}`}
      >
        <LayoutDashboard className="size-4" strokeWidth={2.25} aria-hidden="true" />
        My business
      </Link>
      <button
        type="button"
        onClick={() => {
          onNavigate?.()
          void signOut()
        }}
        className="grid size-11 place-items-center rounded-btn text-ink-muted motion-micro hover:bg-soft hover:text-navy"
      >
        <LogOut className="size-4" strokeWidth={2.25} aria-hidden="true" />
        <span className="sr-only">Sign out</span>
      </button>
    </div>
  )
}
